import {
  LinkedinLogo,
  YoutubeLogo,
  ArrowSquareOut,
  ShieldCheck,
} from "@phosphor-icons/react";
import * as React from "react";
import styles from "./FooterSection.module.css";

const XIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

interface FooterSubSection {
  title: string;
  links: FooterLink[];
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
  subSections?: FooterSubSection[];
}

const FooterLinkComponent: React.FC<{ link: FooterLink }> = ({ link }) => (
  <a
    href={link.href}
    className={styles.link}
    target={link.external ? "_blank" : undefined}
    rel={link.external ? "noopener noreferrer" : undefined}
  >
    {link.label}
    {link.external && (
      <ArrowSquareOut size={12} className={styles.externalIcon} weight="fill" />
    )}
  </a>
);

const footerColumns: FooterColumn[] = [
  {
    title: "Buyers",
    links: [
      { label: "Chrome Extension", href: "/chrome-extension" },
      { label: "Marketplace", href: "/marketplace" },
      { label: "Categories", href: "/categories" },
    ],
    subSections: [
      {
        title: "Developers",
        links: [{ label: "Pricing API", href: "/pricing-api" }],
      },
    ],
  },
  {
    title: "Education",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Customers", href: "/customers" },
      { label: "Buyer Guides", href: "/buyer-guides" },
    ],
    subSections: [
      {
        title: "Life",
        links: [
          { label: "About us", href: "/about" },
          { label: "Careers", href: "/careers" },
        ],
      },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms", href: "/legal/terms-of-use" },
      { label: "Privacy", href: "/legal/privacy-policy" },
      { label: "Subprocessors", href: "/legal/data-subprocessors" },
      { label: "Cookies", href: "/legal/cookie-policy" },
      {
        label: "Data Processing Addendum",
        href: "/legal/data-processing-addendum",
      },
      {
        label: "Master Services Agreement",
        href: "/legal/master-services-agreement",
      },
      { label: "Security", href: "/legal/security" },
      {
        label: "Privacy Request Form",
        href: "/privacy-request-form",
      },
    ],
  },
];

export interface FooterSectionProps {
  className?: string;
}

export const FooterSection: React.FC<FooterSectionProps> = ({ className }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={`${styles.footer} ${className || ""}`.trim()}>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.linksGrid}>
            {footerColumns.map((column) => (
              <div key={column.title} className={styles.linkColumn}>
                <h3 className={styles.columnTitle}>{column.title}</h3>
                <ul className={styles.linkList}>
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <FooterLinkComponent link={link} />
                    </li>
                  ))}
                </ul>
                {column.subSections?.map((subSection) => (
                  <div key={subSection.title} className={styles.subSection}>
                    <h4 className={styles.subSectionTitle}>
                      {subSection.title}
                    </h4>
                    <ul className={styles.linkList}>
                      {subSection.links.map((link) => (
                        <li key={link.label}>
                          <FooterLinkComponent link={link} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {column.title === "Legal" && (
                  <div className={styles.phoneText}>
                    or call{" "}
                    <a href="tel:+18886884404" className={styles.phoneLink}>
                      +1 (888) 688-4404
                    </a>{" "}
                    toll-free
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={styles.rightSection}>
            <div className={styles.socialIcons}>
              <a
                href="https://www.linkedin.com/company/vendr-co"
                className={styles.socialIcon}
                aria-label="LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedinLogo size={18} weight="fill" />
              </a>
              <a
                href="https://twitter.com/vendrhq"
                className={styles.socialIcon}
                aria-label="X (Twitter)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <XIcon size={16} />
              </a>
              <a
                href="https://www.youtube.com/@vendr5034"
                className={styles.socialIcon}
                aria-label="YouTube"
                target="_blank"
                rel="noopener noreferrer"
              >
                <YoutubeLogo size={18} weight="fill" />
              </a>
            </div>
          </div>
        </div>

        <div className={styles.bottomBar}>
          <div className={styles.bottomBarContent}>
            <div className={styles.bottomLeft}>
              <span className={styles.copyright}>
                © {currentYear}. All rights reserved.
              </span>
              <a href="/legal/terms-of-use" className={styles.legalLink}>
                Terms of Service
              </a>
              <a href="/legal/privacy-policy" className={styles.legalLink}>
                Privacy Policy
              </a>
            </div>
            <div className={styles.bottomRight}>
              <span className={styles.soc2Badge}>
                <ShieldCheck size={14} weight="fill" />
                SOC 2 • HIPAA • GDPR
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
